import React, { useState, useEffect } from 'react';
import { primaryButtonSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  DirectionsCar as DirectionsCarIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

interface Asset {
  asset_id: string;
  asset_name: string;
  number_plate?: string | null;
  recorded_hours?: number | null;
  asset_type_name?: string | null;
  location_name?: string | null;
}

const VehicleEvaluation: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [redirectWhenHiddenOff, setRedirectWhenHiddenOff] = useState(false);

  const [valuationEvalSearch, setValuationEvalSearch] = useState('');
  const [valuationDialog, setValuationDialog] = useState<{
    open: boolean;
    asset: Asset | null;
    phase: 'loading' | 'results' | 'history';
    result: any;
    history: any[];
    animProgress: number;
  }>({ open: false, asset: null, phase: 'loading', result: null, history: [], animProgress: 0 });

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    fetchPermissions();
    fetchAssets();
  }, []);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      if (!token || !businessId) { setPermissionsLoaded(true); return; }
      const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allow = res.data.allow_vehicle_evaluation_tab === 1 || res.data.allow_vehicle_evaluation_tab === true;
      const hideNav =
        res.data.hide_vehicle_evaluation_nav_when_disabled === 1 ||
        res.data.hide_vehicle_evaluation_nav_when_disabled === true;
      setAllowed(allow);
      setRedirectWhenHiddenOff(!allow && hideNav);
    } catch {
      // default denied
    } finally {
      setPermissionsLoaded(true);
    }
  };

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/assets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let list: Asset[] = [];
      if (res.data?.success && Array.isArray(res.data.data)) list = res.data.data;
      else if (Array.isArray(res.data)) list = res.data;
      setAssets(list);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const runValuation = async (asset: Asset) => {
    setValuationDialog({ open: true, asset, phase: 'loading', result: null, history: [], animProgress: 0 });
    try {
      const token = localStorage.getItem('token');
      const [valRes, histRes] = await Promise.all([
        axios.post(`${API_BASE_URL}/assets/${asset.asset_id}/valuation`, {}, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/assets/${asset.asset_id}/valuations`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      await new Promise(r => setTimeout(r, 1800));
      setValuationDialog(prev => ({
        ...prev,
        phase: 'results',
        result: valRes.data.data,
        history: histRes.data.data || [],
        animProgress: 0,
      }));
      const start = Date.now();
      const duration = 1600;
      const tick = () => {
        const t = Math.min((Date.now() - start) / duration, 1);
        const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        setValuationDialog(prev => ({ ...prev, animProgress: eased }));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } catch (err: any) {
      setValuationDialog(prev => ({ ...prev, open: false }));
      setSnackbar({ open: true, message: err.response?.data?.message || 'Valuation failed', severity: 'error' });
    }
  };

  const fetchValuationHistory = async (asset: Asset) => {
    setValuationDialog({ open: true, asset, phase: 'history', result: null, history: [], animProgress: 0 });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/assets/${asset.asset_id}/valuations`, { headers: { Authorization: `Bearer ${token}` } });
      setValuationDialog(prev => ({ ...prev, history: res.data.data || [] }));
    } catch {
      setValuationDialog(prev => ({ ...prev, history: [] }));
    }
  };

  if (!permissionsLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed && redirectWhenHiddenOff) {
    return <Navigate to="/admin/assets" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 2 }}>
        <LockIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">Vehicle Evaluation is not enabled for this business</Typography>
        <Typography variant="body2" color="text.disabled">A super admin can enable it under Features &amp; Access in Business Settings.</Typography>
      </Box>
    );
  }

  const evalSearch = valuationEvalSearch.toLowerCase();
  const eligible = assets.filter(a => {
    if (evalSearch) {
      return (a.asset_name || '').toLowerCase().includes(evalSearch) ||
        (a.number_plate || '').toLowerCase().includes(evalSearch);
    }
    return true;
  });

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader title="Vehicle Evaluation" subtitle="Fleet" />
      <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3, gap: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
              Assets need a registration plate and recorded mileage to run a valuation.
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search assets…"
            value={valuationEvalSearch}
            onChange={e => setValuationEvalSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: '#94a3b8' }} />,
              endAdornment: valuationEvalSearch ? (
                <IconButton size="small" onClick={() => setValuationEvalSearch('')} sx={{ color: '#94a3b8' }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
            sx={{
              minWidth: 280,
              '& .MuiOutlinedInput-root': {
                background: '#fff',
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb', borderWidth: 2 },
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2.5 }}>
          {eligible.map(asset => {
            const hasPlate = !!asset.number_plate;
            const hasMileage = asset.recorded_hours != null;
            const canRun = hasPlate && hasMileage;
            const missing = !hasPlate && !hasMileage
              ? 'Needs registration plate and mileage'
              : !hasPlate ? 'Needs a registration plate'
              : 'Needs mileage recorded';

            return (
              <Paper
                key={asset.asset_id}
                variant="outlined"
                sx={{
                  p: 2.5, borderRadius: 2.5,
                  background: canRun ? '#fff' : '#f8fafc',
                  borderColor: '#e2e8f0',
                  position: 'relative',
                  opacity: canRun ? 1 : 0.85,
                  filter: canRun ? 'none' : 'grayscale(0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': canRun ? {
                    boxShadow: '0 4px 12px rgba(37,99,235,0.12)',
                    borderColor: '#2563eb',
                    transform: 'translateY(-2px)',
                    background: '#fafbff'
                  } : { background: '#f1f5f9' },
                }}
              >
                {!canRun && (
                  <Chip
                    label={missing}
                    size="small"
                    sx={{
                      position: 'absolute', top: 12, right: 12,
                      fontSize: '0.65rem', height: 22,
                      background: '#eff6ff', color: '#2563eb',
                      border: '1px solid #bfdbfe', fontWeight: 500
                    }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                    background: canRun ? 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' : '#e2e8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: canRun ? '0 2px 8px rgba(37,99,235,0.25)' : 'none',
                    transition: 'all 0.2s',
                  }}>
                    <DirectionsCarIcon sx={{ color: canRun ? '#fff' : '#94a3b8', fontSize: 24 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: canRun ? '#1e293b' : '#64748b', mb: 0.5 }} noWrap>
                      {asset.asset_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {asset.asset_type_name} · {asset.location_name}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
                  {asset.number_plate ? (
                    <Chip label={(asset.number_plate).toUpperCase()} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', height: 24 }} />
                  ) : (
                    <Chip label="No plate" size="small" sx={{ background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0', height: 24 }} />
                  )}
                  {hasMileage ? (
                    <Chip label={`${Number(asset.recorded_hours).toLocaleString()} mi`} size="small" sx={{ background: '#f0fdf4', color: '#059669', border: '1px solid #bbf7d0', height: 24 }} />
                  ) : (
                    <Chip label="No mileage" size="small" sx={{ background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0', height: 24 }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    fullWidth size="medium" variant="contained"
                    disabled={!canRun}
                    onClick={() => runValuation(asset)}
                    sx={primaryButtonSx}
                    startIcon={<AssessmentIcon />}
                  >
                    Run Valuation
                  </Button>
                  {canRun && (
                    <Tooltip title="View valuation history">
                      <IconButton
                        size="small"
                        onClick={() => fetchValuationHistory(asset)}
                        sx={{
                          border: '1px solid #e2e8f0', color: '#64748b',
                          transition: 'all 0.2s ease',
                          '&:hover': { background: '#eff6ff', borderColor: '#2563eb', color: '#2563eb', transform: 'scale(1.05)' }
                        }}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>

      {/* Vehicle Valuation Dialog */}
      <Dialog
        open={valuationDialog.open}
        onClose={valuationDialog.phase === 'loading' ? undefined : () => setValuationDialog(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
        fullScreen={valuationDialog.phase === 'loading'}
        PaperProps={{ sx: { borderRadius: valuationDialog.phase === 'loading' ? 0 : 3, overflow: 'hidden', transition: 'border-radius 0.3s' } }}
      >
        {valuationDialog.phase === 'loading' && (
          <Box sx={{
            width: '100vw', height: '100vh',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(160deg, #eff6ff 0%, #f8fafc 50%, #f0f4ff 100%)',
            position: 'relative', overflow: 'hidden',
          }}>
            {[0, 1, 2].map(i => (
              <Box key={`bg${i}`} sx={{
                position: 'absolute',
                width: 400 + i * 200, height: 400 + i * 200,
                borderRadius: '50%',
                border: `1px solid rgba(37,99,235,${0.06 - i * 0.015})`,
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                '@keyframes bgPulse': { '0%,100%': { transform: 'translate(-50%,-50%) scale(1)', opacity: 0.5 }, '50%': { transform: 'translate(-50%,-50%) scale(1.04)', opacity: 1 } },
                animation: `bgPulse ${4 + i * 1.5}s ease-in-out ${i * 0.7}s infinite`,
              }} />
            ))}
            <Box sx={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 5 }}>
              <Box sx={{
                position: 'absolute', width: 192, height: 192, borderRadius: '50%',
                border: '2.5px solid transparent',
                borderTopColor: '#2563eb', borderRightColor: 'rgba(37,99,235,0.25)',
                '@keyframes spinRing': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                animation: 'spinRing 2.4s linear infinite',
              }} />
              <Box sx={{
                position: 'absolute', width: 162, height: 162, borderRadius: '50%',
                border: '2px solid transparent',
                borderBottomColor: '#7c3aed', borderLeftColor: 'rgba(124,58,237,0.25)',
                animation: 'spinRing 1.8s linear infinite reverse',
              }} />
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{
                  position: 'absolute', borderRadius: '50%',
                  border: `1.5px solid rgba(37,99,235,${0.5 - i * 0.14})`,
                  width: 60 + i * 32, height: 60 + i * 32,
                  '@keyframes pulse': { '0%': { transform: 'scale(0.6)', opacity: 1 }, '100%': { transform: 'scale(1.6)', opacity: 0 } },
                  animation: `pulse 2.2s ease-out ${i * 0.6}s infinite`,
                }} />
              ))}
              <Box sx={{
                width: 84, height: 84, borderRadius: '50%',
                background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2,
                boxShadow: '0 8px 32px rgba(37,99,235,0.35)',
                '@keyframes centralPulse': {
                  '0%,100%': { boxShadow: '0 8px 32px rgba(37,99,235,0.35)', transform: 'scale(1)' },
                  '50%': { boxShadow: '0 12px 48px rgba(37,99,235,0.55)', transform: 'scale(1.05)' },
                },
                animation: 'centralPulse 2s ease-in-out infinite',
              }}>
                <DirectionsCarIcon sx={{ fontSize: 42, color: '#fff' }} />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'center', mb: 4, zIndex: 2 }}>
              <Typography sx={{
                color: '#1e40af', fontWeight: 800, fontSize: '1.75rem', letterSpacing: 2,
                textTransform: 'uppercase', lineHeight: 1,
                '@keyframes titleFade': { '0%,100%': { opacity: 0.85 }, '50%': { opacity: 1 } },
                animation: 'titleFade 2s ease-in-out infinite',
              }}>
                Vehicle Valuation
              </Typography>
              <Typography sx={{ color: '#2563eb', mt: 1, fontSize: '1rem', fontWeight: 700, letterSpacing: 2, fontFamily: 'monospace' }}>
                {(valuationDialog.asset?.number_plate || '').toUpperCase()}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.5 }}>
                {valuationDialog.asset?.asset_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, zIndex: 2, minWidth: 320, maxWidth: 400 }}>
              {[
                { text: 'Connecting to valuation database', icon: '🔗' },
                { text: 'Retrieving live market data', icon: '📊' },
                { text: 'Calculating current market values', icon: '💰' },
                { text: 'Compiling full breakdown', icon: '📋' },
              ].map((item, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  px: 2.5, py: 1.25, borderRadius: 2,
                  background: '#fff',
                  border: '1px solid rgba(37,99,235,0.15)',
                  boxShadow: '0 1px 4px rgba(37,99,235,0.06)',
                  '@keyframes stepFadeIn': { '0%': { opacity: 0, transform: 'translateX(-16px)' }, '100%': { opacity: 1, transform: 'translateX(0)' } },
                  animation: `stepFadeIn 0.5s ease-out ${0.3 + i * 0.35}s both`,
                }}>
                  <Box sx={{ fontSize: '1.1rem' }}>{item.icon}</Box>
                  <Typography sx={{ color: '#1e293b', fontSize: '0.875rem', flex: 1 }}>{item.text}</Typography>
                  <CircularProgress size={14} sx={{ color: '#2563eb' }} />
                </Box>
              ))}
            </Box>
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: '#e2e8f0' }}>
              <Box sx={{
                height: '100%',
                background: 'linear-gradient(90deg, #2563eb, #7c3aed, #2563eb)',
                backgroundSize: '200% 100%',
                '@keyframes progressSlide': { '0%': { backgroundPosition: '0% 0%', width: '15%' }, '50%': { backgroundPosition: '100% 0%', width: '70%' }, '100%': { backgroundPosition: '200% 0%', width: '95%' } },
                animation: 'progressSlide 2s ease-in-out forwards',
              }} />
            </Box>
          </Box>
        )}

        {valuationDialog.phase === 'results' && valuationDialog.result && (() => {
          const r = valuationDialog.result;
          const p = valuationDialog.animProgress;
          const animated = (val?: number | null) => val != null ? `£${Math.floor(val * p).toLocaleString('en-GB')}` : '—';
          const heroValues = [
            { label: 'Private Clean Sale', sub: 'Best private sale price', value: r.private_clean, color: '#2563eb', bg: '#eff6ff', icon: '🏷️' },
            { label: 'Dealer Forecourt', sub: 'Retail asking price', value: r.dealer_forecourt, color: '#7c3aed', bg: '#f5f3ff', icon: '🏢' },
            { label: 'Part Exchange', sub: 'Trade-in value', value: r.part_exchange, color: '#059669', bg: '#ecfdf5', icon: '🔄' },
            { label: 'Auction Value', sub: 'Minimum floor price', value: r.auction, color: '#d97706', bg: '#fffbeb', icon: '🔨' },
          ];
          const extraValues = [
            { label: 'OTR New Price', value: r.otr },
            { label: 'Trade Retail', value: r.trade_retail },
            { label: 'Private Average', value: r.private_average },
            { label: 'Trade Average', value: r.trade_average },
            { label: 'Trade Poor', value: r.trade_poor },
          ];
          const minVal = Math.min(...[r.auction, r.part_exchange].filter(Boolean));
          const maxVal = Math.max(...[r.dealer_forecourt, r.trade_retail].filter(Boolean));
          const rangeWidth = maxVal - minVal || 1;
          const pxBar = (v?: number | null) => v != null ? Math.max(0, Math.min(100, ((v - minVal) / rangeWidth) * 100)) : 0;

          return (
            <Box sx={{ background: '#f8fafc' }}>
              <Box sx={{ p: 3, pb: 2, background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)', borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ px: 2, py: 0.75, borderRadius: 1.5, background: '#f3d03e', border: '3px solid #1e293b', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '1.2rem', color: '#1e293b', letterSpacing: 3 }}>
                      {r.registration_number}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ color: '#1e293b', fontWeight: 700, lineHeight: 1.2 }}>
                      {r.vehicle_description || valuationDialog.asset?.asset_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {Number(r.mileage).toLocaleString()} miles · Valued {r.valuation_time ? new Date(r.valuation_time).toLocaleDateString('en-GB') : 'just now'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 1.5, p: 2.5 }}>
                {heroValues.map(({ label, sub, value, color, bg, icon }) => (
                  <Box key={label} sx={{ p: 2, borderRadius: 2, background: bg, border: `1px solid ${color}30`, position: 'relative', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <Box sx={{ position: 'absolute', top: 10, right: 12, fontSize: '1.4rem', opacity: 0.3 }}>{icon}</Box>
                    <Typography variant="caption" sx={{ color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block' }}>{label}</Typography>
                    <Typography sx={{ color: '#1e293b', fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{animated(value)}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>{sub}</Typography>
                  </Box>
                ))}
              </Box>
              {r.private_clean && r.dealer_forecourt && (
                <Box sx={{ px: 2.5, mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Price Range</Typography>
                  <Box sx={{ position: 'relative', height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'visible' }}>
                    <Box sx={{
                      position: 'absolute', height: '100%', borderRadius: 4,
                      left: `${pxBar(r.auction) * p}%`,
                      width: `${(pxBar(r.dealer_forecourt) - pxBar(r.auction)) * p}%`,
                      background: 'linear-gradient(90deg, #d97706, #2563eb, #7c3aed)',
                      transition: 'all 0.05s',
                    }} />
                    {[
                      { val: r.auction, label: 'Auction', color: '#d97706' },
                      { val: r.private_clean, label: 'Private', color: '#2563eb' },
                      { val: r.dealer_forecourt, label: 'Dealer', color: '#7c3aed' },
                    ].map(({ val, label, color }) => (
                      <Box key={label} sx={{ position: 'absolute', top: -22, transform: 'translateX(-50%)', left: `${pxBar(val) * p}%`, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color, fontWeight: 700, fontSize: '0.6rem', whiteSpace: 'nowrap' }}>{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700 }}>{animated(r.auction)}</Typography>
                    <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700 }}>{animated(r.dealer_forecourt)}</Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ px: 2.5, pb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1, fontWeight: 600 }}>Full Breakdown</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {extraValues.filter(v => v.value).map(({ label, value }) => (
                    <Box key={label} sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', fontWeight: 600 }}>{label}</Typography>
                      <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{animated(value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ p: 2.5, pt: 1, display: 'flex', gap: 1.5, borderTop: '1px solid #e2e8f0' }}>
                <Button variant="outlined" size="small" startIcon={<HistoryIcon />} onClick={() => setValuationDialog(prev => ({ ...prev, phase: 'history' }))}>History</Button>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => valuationDialog.asset && runValuation(valuationDialog.asset)}>Re-run</Button>
                <Button variant="contained" size="small" onClick={() => setValuationDialog(prev => ({ ...prev, open: false }))} sx={{ ml: 'auto' }}>Close</Button>
              </Box>
            </Box>
          );
        })()}

        {valuationDialog.phase === 'history' && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> Valuation History — {valuationDialog.asset?.asset_name}
            </DialogTitle>
            <DialogContent dividers>
              {valuationDialog.history.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No valuations recorded yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Mileage</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Private Clean</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Part Exchange</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Dealer Retail</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Auction</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {valuationDialog.history.map((v: any) => (
                        <TableRow key={v.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                          <TableCell>
                            <Typography variant="body2">{new Date(v.fetched_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</Typography>
                            <Typography variant="caption" color="text.secondary">{v.vehicle_description}</Typography>
                          </TableCell>
                          <TableCell><Typography variant="body2">{Number(v.mileage).toLocaleString()} mi</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>£{Number(v.private_clean).toLocaleString()}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="secondary">£{Number(v.part_exchange).toLocaleString()}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="secondary">£{Number(v.dealer_forecourt).toLocaleString()}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>£{Number(v.auction).toLocaleString()}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              {valuationDialog.asset && (
                <Button startIcon={<AssessmentIcon />} onClick={() => valuationDialog.asset && runValuation(valuationDialog.asset)} variant="contained">
                  Run New Valuation
                </Button>
              )}
              <Button onClick={() => setValuationDialog(prev => ({ ...prev, open: false }))}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleEvaluation;
